import { useState, ChangeEventHandler } from 'react'
import CateringInput from '@/Components/DuplicateForm/CateringInput'
import { useFacilitiesQuery } from '@/Views/FormSteps/Facilities/Base/Facilities/FacilitiesInput.generated'
import throwGQLError from '@/Utilities/Urql/Errors/throwGQLError'

interface FacilitiesSectionProps {
  eventTypeCollection?: string
  initialFacilityIds?: number[]
  initialCateringNeeds?: string
  onChange: (facilityIds: number[], cateringNeeds: string) => void
}

const FacilitiesSection = ({
  eventTypeCollection,
  initialFacilityIds = [],
  initialCateringNeeds = '',
  onChange,
}: FacilitiesSectionProps) => {
  const [facilityIds, setFacilityIds] = useState<number[]>(initialFacilityIds)
  const [cateringNeeds, setCateringNeeds] = useState(initialCateringNeeds)

  const [{ error, data }] = useFacilitiesQuery({
    pause: !eventTypeCollection,
    requestPolicy: 'cache-first',
    variables: { id: eventTypeCollection ?? '' },
  })

  if (error) throwGQLError(error)

  const facilities = data?.collection?.features ?? []

  const onFacilityChange: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
    const id = parseInt(target.value, 10)
    const next = target.checked ? [...facilityIds, id] : facilityIds.filter((fId) => fId !== id)
    setFacilityIds(next)
    onChange(next, cateringNeeds)
  }

  const onCateringChange: ChangeEventHandler<HTMLTextAreaElement> = ({ target }) => {
    setCateringNeeds(target.value)
    onChange(facilityIds, target.value)
  }

  return (
    <div className='flex flex-col gap-4'>
      <p className='!font-bold text-sm sm:text-base text-black leading-6'>
        Do you need anything specific?
      </p>
      {!eventTypeCollection ? (
        <p className='text-sm text-[#8c8c8c] leading-5'>
          Select an event type above to see facility options.
        </p>
      ) : (
        <>
          <div className='grid grid-flow-col grid-rows-5 gap-x-2 sm:gap-x-4 gap-y-3 w-full'>
            {facilities.map(({ id, name }) => (
              <label key={id} className='flex items-center gap-2 cursor-pointer select-none'>
                <input
                  type='checkbox'
                  name='duplicate-facility'
                  value={id}
                  checked={facilityIds.includes(parseInt(id, 10))}
                  onChange={onFacilityChange}
                  className='w-5 h-5 cursor-pointer accent-black shrink-0'
                />
                <span className='text-sm sm:text-base text-[#1f1f1f] leading-6'>{name}</span>
              </label>
            ))}
          </div>
          <CateringInput cateringNeeds={cateringNeeds} onChange={onCateringChange} />
        </>
      )}
    </div>
  )
}

export default FacilitiesSection
